import type { FetchMatcher, MatcherFixture } from 'tio-data/demo/types';
import type { EnhancedSearchField } from 'tio-reporting/utils/field-filter-pipeline';
import { getEssentialFields } from 'tio-reporting/utils/dataset-utils';
import { getDemoTaProgramsForClient } from 'tio-employee/utils/career-pathways-demo-data';
import { matchesSearchEndpoint, type DemoFixtureIdentity } from './types';
import { toRawDoc } from './identity-fixtures';
import {
  buildExportCsv,
  formatExportCell,
  matchesQuery,
  runDemoSearch,
  scrollDemoSearch,
  type DemoColumnFormat,
} from './reporting-search';

const field = (
  name: string,
  label: string,
  type = 'keyword',
  currencyCents = false
): EnhancedSearchField => ({
  name,
  path: name,
  resultKey: name,
  label,
  type,
  currencyCents,
  isDisplayable: true,
  isSortable: true,
  isAggregatable: true,
});

const DATASETS = {
  'demo-report-tas': {
    modelType: 'TasApplication',
    companyFilterField: 'company_id_keyword',
    fields: [
      field('employee_name_keyword', 'Employee'),
      field('employee_payroll_id_keyword', 'Employee ID'),
      field('tas_application_id_keyword', 'Application'),
      field('tas_application_state_keyword', 'Status'),
      field('tas_application_created_at_date', 'Submitted', 'date'),
    ],
  },
  'demo-report-tas-programs': {
    modelType: 'TasProgramInstance',
    companyFilterField: 'company_id_keyword',
    fields: [
      field('employee_name_keyword', 'Employee'),
      field('employee_payroll_id_keyword', 'Employee ID'),
      field('tas_program_instance_id_keyword', 'Program ID'),
      field('tas_program_template_field_program_name_keyword', 'Program'),
      field('tas_program_instance_field_school_institution_name_keyword', 'Institution'),
      field('tas_program_instance_state_keyword', 'Program Status'),
      field('tas_program_instance_created_at_date', 'Created', 'date'),
      field('tas_application_id_keyword', 'Application'),
      field('tas_application_state_keyword', 'Application Status'),
      field('tas_application_created_at_date', 'Submitted', 'date'),
    ],
  },
  'demo-report-slr': {
    modelType: 'AccountingTransaction',
    companyFilterField: 'slr_transaction_company_id_keyword',
    fields: [
      field('slr_transaction_employee_name_keyword', 'Employee'),
      field('slr_transaction_transaction_date_date', 'Payment Date', 'date'),
      field('slr_transaction_effective_period_keyword', 'Benefit Month'),
      field('slr_transaction_amount_integer', 'Contribution', 'integer', true),
      field('slr_transaction_servicer_name_keyword', 'Servicer'),
      field('slr_transaction_state_keyword', 'Status'),
    ],
  },
  'demo-report-pslf': {
    modelType: 'PslfForm',
    companyFilterField: 'pslf_form_company_id_keyword',
    fields: [
      field('pslf_form_borrower_name_keyword_analyzable', 'Employee'),
      field('pslf_form_employer_name_keyword_analyzable', 'Employer'),
      field('pslf_form_status_keyword', 'Status'),
      field('pslf_form_created_at_date', 'Created', 'date'),
    ],
  },
  'demo-report-core': {
    modelType: 'Person',
    companyFilterField: 'company_ids',
    fields: [
      field('person_first_name_text', 'First Name', 'text'),
      field('person_last_name_text', 'Last Name', 'text'),
      field('all_employee_payroll_ids_text', 'Employee ID', 'text'),
      field('all_company_names_keyword', 'Employer'),
      field('person_is_enrolled_boolean', 'Enrolled', 'boolean'),
    ],
  },
  'demo-report-wellness': {
    modelType: 'Recommendation',
    companyFilterField: 'all_company_ids_keyword',
    fields: [
      field('person_first_name_text', 'First Name', 'text'),
      field('person_last_name_text', 'Last Name', 'text'),
      field('recommendation_requesting_app_keyword', 'Loan Planning Tool'),
      field('recommendation_started_on_date', 'Started', 'date'),
      field('recommendation_completed_boolean', 'Completed', 'boolean'),
    ],
  },
} satisfies Record<
  string,
  { modelType: string; companyFilterField: string; fields: EnhancedSearchField[] }
>;

const datasetFor = (id: string) => DATASETS[id as keyof typeof DATASETS];

const DATASET_CATALOG = {
  data: {
    attributes: {
      result: {
        availableDatasets: [
          {
            slug: 'tas-applications',
            label: 'Tuition Assistance Applications',
            modelType: 'TasApplication',
            product: 'TAS',
          },
          {
            slug: 'tas-program-instances',
            label: 'Tuition Assistance Programs',
            modelType: 'TasProgramInstance',
            product: 'TAS',
          },
          {
            slug: 'slr-transactions',
            label: 'SLR Contributions',
            modelType: 'AccountingTransaction',
            product: 'SLR',
          },
          { slug: 'pslf-forms', label: 'PSLF Forms', modelType: 'PslfForm', product: 'PSLF' },
          { slug: 'people', label: 'Employees', modelType: 'Person', product: 'Core' },
          {
            slug: 'recommendations',
            label: 'Financial Wellness',
            modelType: 'Recommendation',
            product: 'STRATEGYFINDER',
          },
        ],
      },
    },
  },
};

/** Saved viewer configuration for one listed demo report, scoped to the selected employer. */
export function buildDemoReportSearchConfig(id: string, identity: DemoFixtureIdentity) {
  const dataset = datasetFor(id) ?? DATASETS['demo-report-tas'];
  return {
    modelType: dataset.modelType,
    companyId: 'demo-tas-company-1',
    viewType: 'table',
    tableSettings: { selectedColumns: dataset.fields.map((column) => column.name) },
    employerName: identity.employerName,
  };
}

function fieldsForUrl(url: string) {
  const modelType = new URL(url, 'https://demo.invalid').searchParams.get('model-type');
  const dataset = Object.values(DATASETS).find((entry) => entry.modelType === modelType);
  return dataset
    ? {
        fields: dataset.fields,
        datasetConfig: {
          companyFilterField: dataset.companyFilterField,
          defaultColumns: dataset.fields.map((column) => column.name),
        },
      }
    : { fields: [] };
}

function reportRows(identity: DemoFixtureIdentity, modelType: string): Record<string, unknown>[] {
  const people = [
    ['Jordan', 'Lee', 'EMP-1001'],
    ['Priya', 'Natarajan', 'EMP-1042'],
    ['Marcus', 'Brown', 'EMP-1088'],
    ['Sofia', 'Alvarez', 'EMP-1112'],
  ];
  if (modelType === 'AccountingTransaction') {
    return ['2026-05-14', '2026-04-14', '2026-03-14'].flatMap((date, month) =>
      people
        .filter(([first]) => first !== 'Marcus')
        .map(([first, last, payroll], index) => ({
          id: `demo-slr-report-${month}-${payroll}`,
          slr_transaction_company_id_keyword: 'demo-tas-company-1',
          slr_transaction_employee_name_keyword: `${first} ${last}`,
          slr_transaction_transaction_date_date: date,
          slr_transaction_effective_period_keyword: date.slice(0, 7),
          slr_transaction_amount_integer: index === 2 ? 10000 : 12500,
          slr_transaction_servicer_name_keyword: index === 1 ? 'Nelnet' : 'MOHELA',
          slr_transaction_state_keyword: 'sent',
        }))
    );
  }
  return people
    .map(([first, last, payroll], index) => {
      const name = `${first} ${last}`;
      if (modelType === 'TasApplication' || modelType === 'TasProgramInstance') {
        return {
          id: `demo-queue-${index + 1}-application`,
          company_id_keyword: 'demo-tas-company-1',
          company_id: 'demo-tas-company-1',
          employee_name_keyword: name,
          employee_payroll_id_keyword: payroll,
          tas_application_id_keyword: `demo-queue-${index + 1}-application`,
          tas_application_state_keyword:
            index === 2
              ? 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL'
              : 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
          tas_application_created_at_date: `2026-05-${20 + index * 2}`,
          ...(modelType === 'TasProgramInstance'
            ? {
                id: `demo-queue-${index + 1}-instance`,
                company_name_keyword: identity.employerName,
                employee_id_keyword: payroll?.slice(4),
                tas_program_instance_id_keyword: `demo-queue-${index + 1}-instance`,
                tas_program_template_id_keyword: 'demo-tas-template-1',
                tas_program_template_field_program_name_keyword: getDemoTaProgramsForClient(
                  identity.clientId
                )[0]?.name,
                tas_program_instance_field_school_institution_name_keyword:
                  'University of North Carolina at Chapel Hill',
                tas_program_instance_state_keyword: 'TAS.ProgramInstanceState.PROGRAM_APPROVED',
                tas_program_instance_created_at_date: `2026-05-${20 + index * 2}`,
              }
            : {}),
        };
      }
      if (modelType === 'PslfForm') {
        return {
          id: `demo-pslf-report-${index}`,
          pslf_form_company_id_keyword: 'demo-tas-company-1',
          pslf_form_borrower_name_keyword_analyzable: name,
          pslf_form_employer_name_keyword_analyzable: identity.employerName,
          pslf_form_status_keyword: [
            'PENDING_EMPLOYER_SIGNATURE',
            'SUBMITTED',
            'COMPLETE',
            'DRAFT',
          ][index],
          pslf_form_created_at_date: `2026-05-${10 + index}`,
        };
      }
      if (modelType === 'Recommendation') {
        return {
          id: `demo-wellness-report-${index}`,
          all_company_ids_keyword: ['demo-tas-company-1'],
          person_first_name_text: first,
          person_last_name_text: last,
          recommendation_requesting_app_keyword: index % 2 ? 'SF-0726' : 'IDR-0726',
          recommendation_started_on_date: `2026-05-${10 + index}`,
          recommendation_completed_boolean: index !== 2,
        };
      }
      return {
        id: `demo-person-report-${index}`,
        company_ids: ['demo-tas-company-1'],
        person_first_name_text: first,
        person_last_name_text: last,
        all_employee_payroll_ids_text: payroll,
        all_company_names_keyword: identity.employerName,
        person_is_enrolled_boolean: index !== 2,
      };
    })
    .slice(0, ['TasApplication', 'TasProgramInstance'].includes(modelType) ? 3 : people.length);
}

interface ReportSearchBody {
  from?: number;
  size?: number;
  sort?: unknown[];
  _source?: unknown;
  aggs?: { employees?: { terms?: { field?: string } } };
}

const requestBody = (body: unknown) =>
  body as
    | {
        'model-type'?: string;
        searchBody?: ReportSearchBody;
        'search-body'?: ReportSearchBody;
      }
    | undefined;
function aggregationFields(value: unknown): string[] {
  if (!value || typeof value !== 'object') return [];
  return Object.entries(value).flatMap(([key, child]) =>
    key === 'field' && typeof child === 'string' ? [child] : aggregationFields(child)
  );
}

const matchesReportSearch = (body: unknown) => {
  const parsed = requestBody(body);
  const query = parsed?.searchBody ?? parsed?.['search-body'];
  const source = query?._source;
  const dataset = Object.values(DATASETS).find(
    (entry) => entry.modelType === parsed?.['model-type']
  );
  if (
    dataset?.modelType === 'TasProgramInstance' &&
    query?.size === 0 &&
    query.aggs?.employees?.terms?.field === 'tas_program_instance_id_keyword'
  ) {
    return true;
  }
  const fields = [
    ...(dataset?.fields.map((column) => column.name) ?? []),
    ...(dataset?.modelType === 'TasProgramInstance'
      ? getEssentialFields('tas-program-instances')
      : []),
  ];
  if (query?.size === 0 && aggregationFields(query.aggs).some((path) => fields.includes(path))) {
    return true;
  }
  return (
    Array.isArray(source) &&
    source.length > 0 &&
    source.every((path) => typeof path === 'string' && fields.includes(path))
  );
};

function reportDocs(identity: DemoFixtureIdentity, modelType: string) {
  return reportRows(identity, modelType).map((source) => ({ id: String(source['id']), source }));
}

function searchResponse(identity: DemoFixtureIdentity, body: unknown) {
  const parsed = requestBody(body);
  const modelType = parsed?.['model-type'] ?? 'Person';
  const query = parsed?.searchBody ?? parsed?.['search-body'];
  return runDemoSearch(reportDocs(identity, modelType), query, modelType);
}

/** Display names shared by demo report listings, exports and scheduled-run history. */
export const DEMO_REPORT_NAMES = {
  'demo-report-tas': 'TAS Applications by Status',
  'demo-report-slr': 'Monthly SLR Contributions',
  'demo-report-pslf': 'PSLF Form Pipeline',
  'demo-report-core': 'Workforce Education Summary',
  'demo-report-wellness': 'Financial Wellness Engagement',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

function exportFile(id: string, identity: DemoFixtureIdentity, body: unknown, asOf?: Date) {
  const dataset = datasetFor(id);
  if (!dataset) throw new Error(`Unknown demo report: ${id}`);
  const options = isRecord(body) ? body : {};
  const requested = options['search_body'];
  const query = isRecord(requested) ? requested : {};
  const requestedColumns = options['column_keys'] ?? query['_source'];
  const columns = Array.isArray(requestedColumns)
    ? requestedColumns.flatMap((name) => {
        const column = dataset.fields.find((field) => field.name === name);
        return column ? [column] : [];
      })
    : dataset.fields;
  const labels = isRecord(options['column_labels']) ? options['column_labels'] : {};
  const formats = isRecord(options['column_formats']) ? options['column_formats'] : {};
  const dateField = dataset.fields.find((column) => column.type === 'date');
  const docs = reportDocs(identity, dataset.modelType).filter(
    ({ source }) =>
      !asOf || !dateField || Date.parse(String(source[dateField.name])) <= asOf.getTime()
  );
  const sources = scrollDemoSearch(docs, query).filter((source) =>
    matchesQuery(source, query['post_filter'])
  );
  const rows = sources.map((source) =>
    columns.map((column) => {
      const override = formats[column.name];
      const format: DemoColumnFormat | undefined = isRecord(override)
        ? override
        : column.currencyCents
          ? {
              type: 'number',
              divisor: 100,
              decimals: 2,
              currency: 'USD',
              useThousandsSeparator: true,
            }
          : column.type === 'date'
            ? { type: 'date' }
            : undefined;
      return formatExportCell(source[column.name], format);
    })
  );
  const csv = buildExportCsv(
    columns.map((column) => String(labels[column.name] ?? column.label)),
    rows
  );
  return { csv, rowCount: rows.length };
}

const DOWNLOAD_URLS = new Map<string, string>();

function csvDownloadUrl(csv: string) {
  const existing = DOWNLOAD_URLS.get(csv);
  if (existing) return existing;
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  DOWNLOAD_URLS.set(csv, url);
  return url;
}

/** Revokes browser-local CSV downloads when the demo session is destroyed. */
export function resetDemoReportDownloads() {
  for (const url of DOWNLOAD_URLS.values()) URL.revokeObjectURL(url);
  DOWNLOAD_URLS.clear();
}

function scheduleRuns(url: string, identity: DemoFixtureIdentity) {
  const params = new URL(url, 'https://demo.invalid').searchParams;
  const companyId = params.get('filter[company_id]');
  const companyIds = ['demo-tas-company-1', `demo-company-${identity.clientId}`];
  if (companyId && !companyIds.includes(companyId)) return { data: [] };
  const now = new Date();
  const requestedReport = params.get('filter[report_id]');
  const requestedSchedule = params.get('filter[report_schedule_id]');
  const currentRun = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 6));
  const latestMonth = now.getUTCMonth() - (currentRun > now ? 1 : 0);
  const data = Object.entries(DEMO_REPORT_NAMES).flatMap(([id, reportName]) => {
    if (requestedReport && requestedReport !== id) return [];
    const reportScheduleId = `demo-schedule-${id}`;
    if (requestedSchedule && requestedSchedule !== reportScheduleId) return [];
    return [0, 1, 2].map((monthsAgo) => {
      const ranAt = new Date(Date.UTC(now.getUTCFullYear(), latestMonth - monthsAgo, 1, 6));
      const failed = id === 'demo-report-slr' && monthsAgo === 1;
      const file = failed ? undefined : exportFile(id, identity, {}, ranAt);
      return {
        type: 'report-schedule-run',
        id: `demo-run-${id}-${ranAt.toISOString().slice(0, 10)}`,
        attributes: {
          reportScheduleId,
          reportId: id,
          companyId: companyId ?? 'demo-tas-company-1',
          reportName,
          status: failed ? 'failed' : 'succeeded',
          rowCount: file?.rowCount ?? null,
          truncated: false,
          ranAt: ranAt.toISOString(),
          downloadUrl: file ? csvDownloadUrl(file.csv) : null,
        },
      };
    });
  });
  data.sort((a, b) => b.attributes.ranAt.localeCompare(a.attributes.ranAt));
  return toRawDoc({ data });
}

function reportStats(url: string, identity: DemoFixtureIdentity) {
  const params = new URL(url, 'https://demo.invalid').searchParams;
  const id = params.get('filter[report_id]') ?? '';
  const companyId = params.get('filter[company_id]');
  return {
    data: {
      total_views: datasetFor(id) ? 24 : 0,
      unique_users: datasetFor(id) ? 4 : 0,
      export_count: datasetFor(id) ? 3 : 0,
      filters_used: Boolean(datasetFor(id)),
      sorts_used: Boolean(datasetFor(id)),
      views_by_company: [],
      ...(companyId &&
      !['demo-tas-company-1', `demo-company-${identity.clientId}`].includes(companyId)
        ? {
            total_views: 0,
            unique_users: 0,
            export_count: 0,
            filters_used: false,
            sorts_used: false,
          }
        : {}),
    },
  };
}

/** Employer report metadata and row reads; register after the directory matcher and before empty search fallbacks. */
export function buildDemoReportMatchers(identity: DemoFixtureIdentity): MatcherFixture[] {
  return [
    {
      matcher: { kind: 'builder', method: 'GET', urlIncludes: '/api/v2/search/datasets' },
      content: DATASET_CATALOG,
    },
    ...Object.values(DATASETS).map((dataset): MatcherFixture => ({
      matcher: {
        kind: 'builder',
        method: 'GET',
        urlMatches: (url) =>
          /\/api\/v2\/search\/fields(?:\?|$)/.test(url) &&
          new URL(url, 'https://demo.invalid').searchParams.get('model-type') === dataset.modelType,
      },
      content: {
        fields: dataset.fields,
        datasetConfig: {
          companyFilterField: dataset.companyFilterField,
          defaultColumns: dataset.fields.map((column) => column.name),
        },
      },
    })),
    {
      matcher: {
        kind: 'builder',
        method: 'POST',
        urlMatches: matchesSearchEndpoint,
        bodyMatches: matchesReportSearch,
      },
      contentFor: (body) => searchResponse(identity, body),
    },
  ];
}

/** Fetch twins for employer report metadata and rows when a viewer read bypasses the request manager. */
export function buildDemoReportFetchMatchers(identity: DemoFixtureIdentity): FetchMatcher[] {
  return [
    { method: 'GET', urlIncludes: '/api/v2/search/datasets', json: DATASET_CATALOG },
    { method: 'GET', urlIncludes: '/api/v2/sql-datasets', json: { data: [] } },
    {
      method: 'GET',
      urlMatches: (url) => /\/api\/v2\/report-schedule-runs(?:\?|$)/.test(url),
      json: (url: string) => scheduleRuns(url, identity),
    },
    {
      method: 'GET',
      urlMatches: (url) => /\/api\/v2\/report-trackings\/stats(?:\?|$)/.test(url),
      json: (url: string) => reportStats(url, identity),
    },
    {
      method: 'POST',
      urlMatches: (url) => {
        const id = /\/api\/v2\/reports\/([^/?]+)\/export(?:\?|$)/.exec(url)?.[1];
        return Boolean(id && datasetFor(id));
      },
      bodyMatches: isRecord,
      json: (url: string, body?: unknown) => {
        const id = /\/reports\/([^/?]+)\/export/.exec(url)?.[1];
        if (!id) throw new Error(`Unknown demo export: ${url}`);
        const file = exportFile(id, identity, body);
        return {
          signed_url: csvDownloadUrl(file.csv),
          filename: `report_${id.replaceAll('-', '_')}.csv`,
          row_count: file.rowCount,
          asset_id: `demo-export-${crypto.randomUUID()}`,
        };
      },
    },
    ...(['POST', 'PATCH'] as const).map((method): FetchMatcher => ({
      method,
      urlMatches: (url) =>
        method === 'POST'
          ? /\/api\/v2\/report-trackings(?:\?|$)/.test(url)
          : /\/api\/v2\/report-trackings\/demo-report-tracking-[^/?]+(?:\?|$)/.test(url),
      status: method === 'POST' ? 201 : 200,
      bodyMatches: isRecord,
      json: (url: string, body?: unknown) => ({
        data: {
          type: 'report-tracking',
          id:
            method === 'POST'
              ? `demo-report-tracking-${crypto.randomUUID()}`
              : /\/report-trackings\/([^/?]+)/.exec(url)?.[1],
          attributes:
            (body as { data?: { attributes?: Record<string, unknown> } } | undefined)?.data
              ?.attributes ?? {},
        },
      }),
    })),
    {
      method: 'GET',
      urlMatches: (url) => /\/api\/v2\/search\/fields(?:\?|$)/.test(url),
      json: fieldsForUrl,
    },
    {
      method: 'POST',
      urlMatches: matchesSearchEndpoint,
      bodyMatches: matchesReportSearch,
      json: (_url: string, body?: unknown) => searchResponse(identity, body),
    },
  ];
}
